import { ProspectList } from '@/types/prospect';
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Edit, Trash2, Copy, Users, AlertTriangle, CheckCircle } from 'lucide-react';
import { format } from 'date-fns';

interface ProspectListCardProps {
  prospectList: ProspectList;
  onEdit: (list: ProspectList) => void;
  onDelete: (id: string) => void;
  onDuplicate: (list: ProspectList) => void;
  onView: (list: ProspectList) => void;
  readOnly?: boolean;
  complianceLabelMode?: 'friendly' | 'default';
}

export function ProspectListCard({ 
  prospectList, 
  onEdit, 
  onDelete, 
  onDuplicate, 
  onView,
  readOnly = false,
  complianceLabelMode = 'default'
}: ProspectListCardProps) {
  const handleCardClick = () => {
    // Handle both camelCase and snake_case formats
    const isCompliant = prospectList.tcpaCompliance || (prospectList as any).tcpa_compliance;
    if (isCompliant) {
      onView(prospectList);
    }
  };

  // Helper function to safely format dates
  const formatDate = (dateValue: any) => {
    if (!dateValue) return 'N/A';
    try {
      const date = new Date(dateValue);
      if (isNaN(date.getTime())) return 'Invalid date';
      return format(date, 'MMM d, yyyy');
    } catch {
      return 'Invalid date';
    }
  };

  // Handle both data formats
  const isCompliant = prospectList.tcpaCompliance || (prospectList as any).tcpa_compliance;
  const acknowledgedAt = prospectList.tcpaAcknowledgedAt || (prospectList as any).tcpa_acknowledged_at;
  const updatedAt = prospectList.updatedAt || (prospectList as any).updated_at;

  return (
    <Card 
       className={`group hover:shadow-md transition-shadow ${
         isCompliant ? 'cursor-pointer' : 'opacity-75'
       }`}
      onClick={handleCardClick}
    >
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <CardTitle className="text-lg font-semibold">{prospectList.name}</CardTitle>
            <div className="flex gap-2 mt-2">
              <Badge 
                variant={isCompliant ? 'default' : 'destructive'}
                className="flex items-center gap-1"
              >
                 {isCompliant ? (
                   <CheckCircle className="h-3 w-3" />
                 ) : (
                   <AlertTriangle className="h-3 w-3" />
                 )}
                 {complianceLabelMode === 'friendly' 
                   ? (isCompliant ? 'Compliance Acknowledged' : 'Compliance Needed')
                   : (isCompliant ? 'TCPA Compliant' : 'TCPA Required')
                 }
              </Badge>
              <Badge variant="outline">
                {prospectList.prospects.length} prospects
              </Badge>
            </div>
          </div>
          <Users className="h-5 w-5 text-muted-foreground" />
        </div>
      </CardHeader>
      
      <CardContent className="pb-3">
        <div className="space-y-2 text-sm text-muted-foreground">
          {prospectList.description && (
            <div><span className="font-medium">Description:</span> {prospectList.description}</div>
          )}
          <div><span className="font-medium">Prospects:</span> {prospectList.prospects.length}</div>
          <div><span className="font-medium">Companies:</span> {
            Array.from(new Set(prospectList.prospects.map(p => p.company))).length
          }</div>
           {isCompliant && acknowledgedAt && (
             <div className="text-xs text-green-600">
               {complianceLabelMode === 'friendly' ? 'Compliance' : 'TCPA'} acknowledged {formatDate(acknowledgedAt)}
             </div>
           )}
        </div>
      </CardContent>
      
      <CardFooter className="pt-3 border-t">
        <div className="flex items-center justify-between w-full">
          <div className="text-xs text-muted-foreground">
            Updated {formatDate(updatedAt)}
          </div>
          {!readOnly && (
            <div 
              className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity"
              onClick={(e) => e.stopPropagation()}
            >
              <Button size="sm" variant="ghost" onClick={() => onDuplicate(prospectList)}>
                <Copy className="h-4 w-4" />
              </Button>
              <Button size="sm" variant="ghost" onClick={() => onEdit(prospectList)}>
                <Edit className="h-4 w-4" />
              </Button>
              <Button 
                size="sm" 
                variant="ghost" 
                onClick={() => onDelete(prospectList.id)}
                className="text-destructive hover:text-destructive"
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
          )}
        </div>
      </CardFooter>
    </Card>
  );
}